import { ChatEnum } from '../../../services/api-names';
import {
  HttpBackend,
  HttpClient,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { map } from 'rxjs/operators';
import { Serializer } from '../../../helpers/serializer/seriealizer';
import { Utils } from '../../../utils/utils';
import { TECH_LIB_PORTAL_CONFIG, TechLibPortalConfig } from '../../../tech-lib-portal.interceptor';
import { SessionService } from './../../../services/session.service';

import { ChatMessageTextAddRequestInterface } from '../request/interfaces/chat-message-text-add-request.interface';
import { ChatTopicsAddRequestInterface } from '../request/interfaces/chat-topics-add-request.interface';
import { ChatReferenceDataResponseInterface } from '../response/interfaces/chat-referencedata-response.interface';
import { ChatTopicsAddResponseInterface } from '../response/interfaces/chat-topics-add-response.interface';
import { ChatTopicsResponseInterface } from '../response/interfaces/chat-topics-response.interface';
import { ChatReferenceDataResponseModel } from '../response/models/chat-referencedata-response.model';
import { ChatTopicsAddResponseModel } from '../response/models/chat-topics-add-response.model';
import { ChatTopicsResponseModel } from '../response/models/chat-topics-response.model';
import { DeleteResponseInterface, DeleteResponseModel } from '../../commons';


@Injectable({
  providedIn: 'root',
})

export class ChatService {
  constructor(
    @Inject(TECH_LIB_PORTAL_CONFIG) private config: TechLibPortalConfig,
    private httpBackend: HttpBackend,
    private httpClient: HttpClient,
    private serializer: Serializer,
    private session: SessionService,
    private zone: NgZone
  ) { }

  public getReferenceData(): Observable<ChatReferenceDataResponseInterface> {
    return new Observable(
      (observer: Observer<ChatReferenceDataResponseModel>) => {
        this.httpClient
          .get<ChatReferenceDataResponseInterface>(
            this.config.baseApi + ChatEnum.api_portal_chatreferencedata
          )
          .pipe(
            map((resp: any) => {
              return resp as ChatReferenceDataResponseModel;
            })
          )
          .subscribe(
            (resp: any) => {
              observer.next(resp);
              observer.complete();
            },
            (err) => observer.error(err)
          );
      }
    );
  }

  public getPortalChatTopics(): Observable<ChatTopicsResponseInterface[]> {
    return new Observable((observer: Observer<ChatTopicsResponseModel[]>) => {
      this.httpClient
        .get<ChatTopicsResponseInterface[]>(
          this.config.baseApi + ChatEnum.api_portal_chattopics
        )
        .pipe(
          map((resp: any) => {
            return resp as ChatTopicsResponseModel[];
          })
        )
        .subscribe(
          (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          (err) => observer.error(err)
        );
    });
  }

  //api_portal_chattopicadd Para añadir un nuevo topic desde cero ChatTopicsAddRequestInterface
  public addPortalChatTopic(
    request: ChatTopicsAddRequestInterface
  ): Observable<ChatTopicsAddResponseInterface> {
    const body = request;
    return new Observable((observer: Observer<ChatTopicsAddResponseModel>) => {
      this.httpClient
        .post<ChatTopicsAddResponseInterface>(
          this.config.baseApi + ChatEnum.api_portal_chattopicadd,
          body
        )
        .pipe(
          map((resp: any) => {
            return new ChatTopicsAddResponseModel(resp);
          })
        )
        .subscribe(
          (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          (err) => observer.error(err)
        );
    });
  }

  //apirest/portal/chat/message/text/add Para añadir mensaje con el topicID
  public addPortalChatMessageText(
    request: ChatMessageTextAddRequestInterface
  ): Observable<ChatTopicsAddResponseInterface> {
    const body = request;
    return new Observable((observer: Observer<ChatTopicsAddResponseModel>) => {
      this.httpClient
        .post<ChatTopicsAddResponseInterface>(
          this.config.baseApi + ChatEnum.api_portal_chatmessagetextadd,
          body
        )
        .pipe(
          map((resp: any) => {
            return new ChatTopicsAddResponseModel(resp);
          })
        )
        .subscribe(
          (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          (err) => observer.error(err)
        );
    });
  }

  public addPortalChatMeeting(
    request: ChatMessageTextAddRequestInterface
  ): Observable<ChatTopicsAddResponseInterface> {

    const body = request;
    return new Observable((observer: Observer<ChatTopicsAddResponseModel>) => {
      this.httpClient
        .post<ChatTopicsAddResponseInterface>(
          this.config.baseApi + ChatEnum.api_portal_chatmeetingadd,
          body
        )
        .pipe(
          map((resp: any) => {
            return new ChatTopicsAddResponseModel(resp);
          })
        )
        .subscribe(
          (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          (err) => observer.error(err)
        );
    });
  }

  //apirest/portal/chat/topic/{topicid}/mesages Para ver los mensajes de una conversación
  public getPortalChatTopicMessages(
    topicId: string,
    numElements: number = 20,
    offset: number = 0
  ): Observable<ChatTopicsResponseInterface> {

    const url = Utils.formatString(
      this.config.baseApi + ChatEnum.api_portal_chattopicmesages,
      [topicId, numElements, offset]
    );

    return new Observable((observer: Observer<ChatTopicsResponseModel>) => {
      this.httpClient
        .get(url)
        .pipe(map((resp: any) => new ChatTopicsResponseModel(resp)))
        .subscribe(
          (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          (err) => observer.error(err)
        );
    });
  }

  //GET apirest/portal/chat/message/text/{messageid}/readed Para ver los mensajes leidos
  public setPortalChatMessageTextReaded(
    messageId: number
  ): Observable<DeleteResponseInterface> {

    const url = Utils.formatString(
      this.config.baseApi +
      ChatEnum.api_portal_chatmessagetextmessageidreaded,
      [messageId]
    );

    return new Observable((observer: Observer<DeleteResponseModel>) => {
      this.httpClient
        .get(url)
        .pipe(
          map((resp: any) =>
            this.serializer.fromJsonArray(resp, DeleteResponseModel)
          )
        )
        .subscribe(
          (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          (err) => observer.error(err)
        );
    });
  }

  //Para adjuntar archivo POST  apirest/portal/chat/message/doc/add
  addDocumentChat(
    formData: FormData
  ): Observable<ChatTopicsAddResponseInterface> {

    const body = formData;
    const httpclientNoHeaders = new HttpClient(this.httpBackend);
    let headers: HttpHeaders = new HttpHeaders();
    //headers = headers.append('Content-Type', 'multipart/form-data');
    headers = headers.append('Tower30-SessionToken', this.config.sessionToken);
    headers = headers.append('Tower30-ClientCode', this.config.clientCode);
    headers = headers.append('Tower30-Domain', this.config.domain);
    headers = headers.append('Tower30-Language', this.config.language);
    headers = headers.append('Tower30-ApiKey', this.config.apiKey);

    let options = { headers: headers };

    return new Observable((observer: Observer<ChatTopicsAddResponseModel>) => {
      httpclientNoHeaders
        .post<ChatTopicsAddResponseInterface>(
          this.config.baseApi + ChatEnum.api_portal_chatmessagedocadd,
          body,
          options
        )
        .pipe(
          map((resp: any) => {
            return new ChatTopicsAddResponseModel(resp);
          })
        )
        .subscribe(
          (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          (err) => observer.error(err)
        );
    });
  }
  //Para obtener un mensaje de tipo documento:GET apirest/portal/chat/message/doc/{messageid}
  getDocumentChat(id: string): Observable<any> {
    const url = Utils.formatString(
      this.config.baseApi + ChatEnum.api_portal_chatmessagedoc,
      [id]
    );

    return new Observable((observer: Observer<any>) => {
      this.httpClient
        .get(url)
        .pipe(
          map((resp: any) => {
            return resp;
          })
        )
        .subscribe(
          (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          (err) => observer.error(err)
        );
    });
  }

  //apirest/portal/chat/{topicid}/message/text/readed
  public getPortalChatTopicIdMessageTextReaded(
    topicId: string
  ): Observable<DeleteResponseInterface> {
    const url = Utils.formatString(
      this.config.baseApi +
      ChatEnum.api_portal_chattopicidmessagetextreaded,
      [topicId]
    );

    return new Observable((observer: Observer<DeleteResponseModel>) => {
      this.httpClient
        .get(url)
        .pipe(map((resp: any) => new DeleteResponseModel(resp)))
        .subscribe(
          (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          (err) => observer.error(err)
        );
    });
  }

  subscribeToSSE(): Observable<any> {
    return new Observable((observer) => {
      const eventSource = this.getEventSource(
        this.config.baseApi + ChatEnum.api_portal_subscribesse
      );
      eventSource.onmessage = (event) => {
        this.zone.run(() => {
          observer.next(event);
        });
      };
      eventSource.onerror = (error) => {
        this.zone.run(() => {
          observer.error(error);
        });
      };
    });
  }

  private getEventSource(url: string): EventSource {
    const fullUrl =
      url +
      '?sessionToken=' +
      this.config.sessionToken +
      '&clientCode=' +
      this.config.clientCode +
      '&domain=' +
      this.config.domain +
      '&language=' +
      this.config.language;

    return new EventSource(fullUrl);
  }

}
